import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cx } from "./cx";
import { IconCheck, IconMinus } from "./icons";
import "./Checkbox.css";

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange" | "size"> {
  checked?: boolean;
  /** Tri-state "some selected"; exposed to AT as aria-checked="mixed". */
  indeterminate?: boolean;
  onChange?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  /** Visible label. Without it pass `aria-label`. */
  label?: ReactNode;
  hint?: ReactNode;
}

/** Native checkbox (keeps form + AT semantics) with a token-styled box. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { checked, indeterminate = false, onChange, label, hint, className, disabled, ...rest },
  forwardedRef,
) {
  const inner = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      inner.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const box = (
    <span className="ui-checkbox__box">
      <input
        ref={setRef}
        type="checkbox"
        className="ui-checkbox__input"
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate ? "mixed" : undefined}
        onChange={(e) => onChange?.(e.target.checked, e)}
        {...rest}
      />
      <IconCheck className="ui-checkbox__mark ui-checkbox__mark--check" />
      <IconMinus className="ui-checkbox__mark ui-checkbox__mark--mixed" />
    </span>
  );

  if (!label && !hint) {
    return <span className={cx("ui-checkbox", disabled && "ui-checkbox--disabled", className)}>{box}</span>;
  }

  return (
    <label className={cx("ui-checkbox", disabled && "ui-checkbox--disabled", className)}>
      {box}
      <span className="ui-checkbox__text">
        {label ? <span className="ui-checkbox__label">{label}</span> : null}
        {hint ? <span className="ui-checkbox__hint">{hint}</span> : null}
      </span>
    </label>
  );
});
